/* IFIORA delivery selection. Local Warsaw dates; cart attributes are the order record.
   2026-10-03 (v2): delivery day/time are OPTIONAL. This module never blocks checkout:
   - validate() always returns true; an impossible selection (past day, Sunday, closed slot) is dropped before checkout,
     a valid day without a time slot is kept (the shop confirms the time);
   - save() never rejects and waits for the cart helper (fixes the "Nie udało się zapisać terminu" message on page load);
   - the checkout submit interceptor fails open: if the cart helper is missing, the native form submit proceeds.
   ROLLBACK: restore the previous version of this file from the theme "22-9-2026". */
(() => {
  'use strict';
  const ZONE = 'Europe/Warsaw';
  const WINDOWS = [
    { value: '10:00-14:00', label: '10:00–14:00', end: 14 * 60 },
    { value: '12:00-16:00', label: '12:00–16:00', end: 16 * 60 },
    { value: '16:00-20:00', label: '16:00–20:00', end: 20 * 60 }
  ];
  function clock(now = new Date()) {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }).formatToParts(now).map(x => [x.type, x.value]));
    return { date: `${p.year}-${p.month}-${p.day}`, minutes: +p.hour * 60 + +p.minute + +p.second / 60 };
  }
  function validDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const d = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
  }
  const sunday = date => new Date(`${date}T12:00:00Z`).getUTCDay() === 0;
  function nextDate(date) {
    const d = new Date(`${date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  }
  function slots(date, now = new Date()) {
    const current = clock(now);
    if (!validDate(date) || date < current.date || sunday(date)) return [];
    if (date !== current.date) return WINDOWS;
    if (current.minutes >= 16 * 60) return [];
    const ready = Math.max(current.minutes, 10 * 60) + 4 * 60;
    return WINDOWS.filter(slot => slot.end >= ready);
  }
  function firstDate(now = new Date()) {
    let date = clock(now).date;
    while (!slots(date, now).length) date = nextDate(date);
    return date;
  }
  const rules = { clock, slots, firstDate, validDate };
  if (typeof module !== 'undefined' && module.exports) module.exports = rules;
  if (typeof window === 'undefined' || window.IFIORADelivery) return;

  const components = new Set();
  const state = { date: '', time: '', initialized: false, busy: false, error: '', touched: false };
  let pending = Promise.resolve();
  let savesInFlight = 0;
  let saveGeneration = 0;

  function helper() {
    const alpine = window.Alpine;
    const store = alpine && typeof alpine.store === 'function' ? alpine.store('xCartHelper') : null;
    return store && typeof store.updateCart === 'function' ? store : null;
  }
  /* The cart helper is registered on alpine:init, which can run after this element connects. Wait for it instead of failing. */
  function waitForHelper(timeout = 8000) {
    return new Promise(resolve => {
      const started = Date.now();
      (function poll() {
        const cart = helper();
        if (cart) return resolve(cart);
        if (Date.now() - started >= timeout) return resolve(null);
        setTimeout(poll, 100);
      })();
    });
  }
  function dateOk() { return validDate(state.date) && slots(state.date).length > 0; }
  function timeOk() { return slots(state.date).some(slot => slot.value === state.time); }
  function complete() { return dateOk() && timeOk(); }
  /* Optional field policy: keep a deliverable day (with or without a time slot); drop anything impossible. */
  function normalise() {
    if (!state.date && !state.time) return false;
    const before = state.date + '|' + state.time;
    if (!dateOk()) { state.date = ''; state.time = ''; }
    else if (state.time && !timeOk()) state.time = '';
    return before !== state.date + '|' + state.time;
  }
  function render() { components.forEach(component => component.paint()); }
  function attributes() {
    return { 'Delivery date': state.date, 'Delivery time': state.time,
      'Delivery timezone': state.date ? ZONE : '' };
  }
  /* Never rejects. quiet = automatic save (no error message shown on failure). */
  function save(options) {
    const quiet = Boolean(options && options.quiet);
    const snapshot = attributes();
    const generation = ++saveGeneration;
    savesInFlight += 1;
    state.busy = true;
    if (!quiet) state.error = '';
    render();
    pending = pending.catch(() => {}).then(async () => {
      const cart = await waitForHelper();
      if (!cart) throw new Error('Cart unavailable');
      await cart.updateCart({ attributes: snapshot });
      if (generation === saveGeneration) state.error = '';
    }).catch(() => {
      if (generation === saveGeneration && !quiet) state.error = 'save_error';
    }).finally(() => { savesInFlight -= 1; state.busy = savesInFlight > 0; render(); });
    return pending;
  }
  /* Always true: the delivery slot is optional. On a checkout attempt the selection is normalised and the
     inline message cleared, so nothing here can stop the customer. */
  function validate(showError = false) {
    if (!components.size) return true;
    if (showError) {
      normalise();
      state.error = '';
      render();
    }
    return true;
  }
  window.IFIORADelivery = { rules, save, validate };
  document.addEventListener('eurus:cart:validate', event => {
    validate(Boolean(event.detail && event.detail.isCheckOut));
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!['cart', 'CartDrawer-Form'].includes(form.id)) return;
    if (form.dataset.ifioraDeliveryReady === 'true') {
      delete form.dataset.ifioraDeliveryReady;
      return;
    }
    if (event.submitter && event.submitter.name !== 'checkout') return;
    const cart = helper();
    if (!cart || typeof cart.goToCheckout !== 'function') return; /* fail open: native submit carries the fields */
    event.preventDefault();
    cart.goToCheckout(event);
  }, true);

  class DeliveryScheduler extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dateInput = this.querySelector('[data-date]');
      this.timeInput = this.querySelector('[data-time]');
      if (!this.dateInput || !this.timeInput) { this.ready = false; return; }
      if (!state.initialized) {
        state.date = this.dataset.date || '';
        state.time = this.dataset.time || '';
        state.initialized = true;
        const first = firstDate();
        if (!validDate(state.date) || state.date < first || !slots(state.date).length) {
          state.date = first;
          state.time = '';
          this.needsSave = true;
        }
        const available = slots(state.date);
        if (!available.some(slot => slot.value === state.time)) {
          state.time = available.length ? available[0].value : '';
          this.needsSave = true;
        }
      }
      components.add(this);
      this.dateInput.addEventListener('change', () => {
        state.date = this.dateInput.value;
        state.touched = true;
        const available = slots(state.date);
        if (!available.some(slot => slot.value === state.time)) state.time = available.length ? available[0].value : '';
        state.error = state.date && !slots(state.date).length ? 'unavailable' : '';
        render();
        save();
      });
      this.timeInput.addEventListener('change', () => {
        state.time = this.timeInput.value;
        state.touched = true;
        state.error = '';
        render();
        save();
      });
      this.paint();
      if (this.needsSave) { this.needsSave = false; save({ quiet: true }); }
    }
    disconnectedCallback() { components.delete(this); this.ready = false; }
    paint() {
      const choices = slots(state.date);
      this.dateInput.min = firstDate();
      this.dateInput.value = state.date;
      this.timeInput.replaceChildren(new Option(this.dataset.selectTime, ''));
      choices.forEach(slot => this.timeInput.add(new Option(slot.label, slot.value)));
      this.timeInput.value = choices.some(slot => slot.value === state.time) ? state.time : '';
      this.timeInput.disabled = !choices.length;
      const error = this.querySelector('[data-error]');
      const key = state.error || (state.touched && state.date && !choices.length ? 'unavailable' : '');
      if (error) {
        error.textContent = key ? this.dataset[key === 'save_error' ? 'saveError' : key] : '';
        error.hidden = !key;
      }
      this.dateInput.setAttribute('aria-invalid', String(key === 'unavailable'));
      this.timeInput.setAttribute('aria-invalid', 'false');
      const status = this.querySelector('[data-save-status]');
      if (status) status.textContent = state.busy ? this.dataset.saving : (complete() && !key ? this.dataset.saved : '');
    }
  }
  customElements.define('ifiora-delivery-scheduler', DeliveryScheduler);
})();